import React, { useCallback } from 'react';
import classNames from 'classnames';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';

// The purpose of this light context and accompanying component is so that we can delay the loading
// of content around a Lottie until the it has loaded asynchronously. This allows us to avoid a
// janky experience where the content renders -> then the Lottie loads -> then the Lottie pushes the
// content to a different spot on the page

const LottieLoadingContext = React.createContext(undefined);

// Making this global is nice because you don't need to worry about having N different caches that all need to fetch the same lotties.
// This holds promises not the final value, but you can await the promises for the final value
const lottiePromises = new Map();

// Caching lotties is great, but react-lottie duplicates DOM nodes when you render the exact same lottie layers more than once.
// A <1k DOM node lottie could turn into a >5k DOM node lottie with a few re-renders.
// Shallow copying layers by using .slice(0) doesn't work either - you need the layers themselves to be copied.
//
// https://github.com/airbnb/lottie-web/issues/2151 - Why are we doing the JSON parse and stringify here?
// There is a known issue with Lotties that use repeaters in lottie-web which is used by react-lottie.
// If a Lotties using repeaters appears 2+ times it will appear broken after the first render. We need to use
// a deep copy of the lottie for this reason and the reason mentioned above. JSON.parse() is safe to use here because
// by definition Lotties must be JSON objects and are therefore JSON serializable.
//
// In our testing, there is more than just the layers that needs to be deep copied, so we are deep copying the entire lottie.
// Without this, on multiple re-renders lotties that use repeaters and other features that mutate the data will appear broken.
const copyLottie = (lottie) => JSON.parse(JSON.stringify(lottie));


function LottieLoadingProvider({ children }) {
  const [isContentShowing, setIsContentShowing] = React.useState(true);

  const fetchLottie = useCallback((url) => {
    if (lottiePromises.has(url)) {
      return lottiePromises.get(url).then(copyLottie);
    }
    const promise = fetch(url).then(res => res.json());
    lottiePromises.set(url, promise);
    return promise.then(copyLottie);
  }, []);

  const memoizedValue = React.useMemo(() => {
    return {
      isContentShowing,
      setIsContentShowing,
      fetchLottie,
    };
  }, [isContentShowing, setIsContentShowing]);

  return (
    <LottieLoadingContext.Provider value={memoizedValue}>
      {children}
    </LottieLoadingContext.Provider>
  );
}
LottieLoadingProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export const useLottieLoadingContext = () => {
  const context = React.useContext(LottieLoadingContext);

  if (context === undefined) {
    throw new Error('useLottieLoading must be used within a LottieLoadingProvider');
  }

  return context;
};

const useStyles = buildUseStyles('LottieLoadingLayout', (() => ({
  rootHidden: {
    display: 'none',
  },
})));

const LottieLoadingContent = ({ children }) => {
  const { isContentShowing } = useLottieLoadingContext();
  const classes = useStyles();

  return (
    <div className={classNames({ [classes.rootHidden]: !isContentShowing })}>
      {children}
    </div>
  );
};

LottieLoadingContent.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export const LottieLoadingLayout = ({ children }) => (
  <LottieLoadingProvider>
    <LottieLoadingContent>{children}</LottieLoadingContent>
  </LottieLoadingProvider>
);
LottieLoadingLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};
